// react
import { MouseEvent, useContext, useEffect, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// mui
import { CircularProgress, Container, Grid, Paper, Typography } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// types
import { Notification, NotificationSetting, NotificationType } from '../../../../../@types';

// fields
import { Switch } from '../../../../fields/Switch/Switch';

// ui
import { BtnBox } from '../../../../ui/BtnBox/BtnBox';
import { IconCircleProps } from '../../../../ui/IconCircle/IconCircle';

// dialogs
import { DialogUpdateUserNotifications } from '../../../../dialogs/DialogUpdateUserNotifications/DialogUpdateUserNotifications';

// context
import { ContextUser } from '../../../../../App.context';
import { ContextProject } from '../../Project.context';

// utils
import { toCamelCase } from '../../../../../utils/strings';

// theme
import { theme } from '../../../../../theme';

// config
import { featureFlags } from '../../../../../config/featureFlags';

const getNotificationMetadata = (
	notifications: Notification[],
	t: (key: string) => string
): NotificationSetting[] => {
	const notificationMapping: Record<
		NotificationType,
		{ category: string; icon: IconCircleProps['type'] }
	> = {
		// Entities
		AUDITOR_CREATED: { category: 'entities', icon: 'auditor' },
		BENEFICIARY_CREATED: { category: 'entities', icon: 'beneficiary' },
		BORROWER_CREATED: { category: 'entities', icon: 'borrower' },
		DONOR_CREATED: { category: 'entities', icon: 'donor' },
		ENTITY_CREATED: { category: 'entities', icon: 'entity' },
		INTERMEDIARY_CREATED: { category: 'entities', icon: 'entity' },
		PIU_CREATED: { category: 'entities', icon: 'entity' },
		SUB_PIU_CREATED: { category: 'entities', icon: 'subPiu' },
		SUPPLIER_CREATED: { category: 'entities', icon: 'supplier' },
		TAX_AUTHORITY_CREATED: { category: 'entities', icon: 'tax' },
		// Transactions
		CASH_BENEFIT_CREATED: { category: 'transactions', icon: 'cashBenefit' },
		CASH_BENEFIT_CONFIRMED: { category: 'transactions', icon: 'cashBenefit' },
		NON_CASH_BENEFIT_CREATED: { category: 'transactions', icon: 'nonCashBenefit' },
		OPEX_CREATED: { category: 'transactions', icon: 'opex' },
		PAYMENT_CREATED: { category: 'transactions', icon: 'payment' },
		PAYMENT_CONFIRMED: { category: 'transactions', icon: 'payment' },
		REPLENISHMENT_CREATED: { category: 'transactions', icon: 'replenishment' },
		TRANSFER_CREATED: { category: 'transactions', icon: 'transfer' },
		// Documents
		DOCUMENT_CREATED: { category: 'documents', icon: 'document' },
		DOCUMENT_UPDATED: { category: 'documents', icon: 'document' },
		// ACTS
		ACT_CREATED: { category: 'activities', icon: 'act' },
		// Accounts
		ACCOUNT_CREATED: { category: 'accounts', icon: 'account' },
		// Contracts
		CONTRACT_APPROVED: { category: 'contracts', icon: 'v2Contract' },
		CONTRACT_CREATED: { category: 'contracts', icon: 'contract' },
		CONTRACT_PENDING_APPROVAL: { category: 'contracts', icon: 'v2Contract' },
		CONTRACT_REJECTED: { category: 'contracts', icon: 'v2Contract' },
		CONTRACT_RETURNED: { category: 'contracts', icon: 'v2Contract' },
		CONTRACT_UPDATED: { category: 'contracts', icon: 'contract' },
		// Invoices / vouchers (V2 maker-checker)
		INVOICE_APPROVED: { category: 'transactions', icon: 'v2Invoice' },
		INVOICE_CREATED: { category: 'transactions', icon: 'v2Invoice' },
		INVOICE_PAID: { category: 'transactions', icon: 'v2Invoice' },
		INVOICE_PENDING_APPROVAL: { category: 'transactions', icon: 'v2Invoice' },
		INVOICE_REJECTED: { category: 'transactions', icon: 'v2Invoice' },
		PAYMENT_DISPUTED: { category: 'transactions', icon: 'v2Voucher' },
		PAYMENT_POSTED: { category: 'transactions', icon: 'v2Voucher' },
		VOUCHER_FINAL_APPROVED: { category: 'transactions', icon: 'v2Voucher' },
		VOUCHER_PENDING_APPROVAL: { category: 'transactions', icon: 'v2Voucher' },
		VOUCHER_REJECTED: { category: 'transactions', icon: 'v2Voucher' },
		// Planning (V2 maker-checker)
		WORK_PLAN_APPROVED: { category: 'planning', icon: 'v2WorkPlan' },
		WORK_PLAN_PENDING_APPROVAL: { category: 'planning', icon: 'v2WorkPlan' },
		WORK_PLAN_REJECTED: { category: 'planning', icon: 'v2WorkPlan' },
		SPEND_PLAN_APPROVED: { category: 'planning', icon: 'v2SpendPlan' },
		SPEND_PLAN_PENDING_APPROVAL: { category: 'planning', icon: 'v2SpendPlan' },
		SPEND_PLAN_REJECTED: { category: 'planning', icon: 'v2SpendPlan' },
		// Condition requirements (V2)
		CONDITION_REQUIREMENT_APPROVED: { category: 'contracts', icon: 'v2Condition' },
		CONDITION_REQUIREMENT_ASSIGNED: { category: 'contracts', icon: 'v2Condition' },
		CONDITION_REQUIREMENT_REJECTED: { category: 'contracts', icon: 'v2Condition' },
		CONDITION_REQUIREMENT_SUBMITTED: { category: 'contracts', icon: 'v2Condition' },
		// Settings
		CATEGORY_CREATED: { category: 'settings', icon: 'category' },
		CATEGORY_UPDATED: { category: 'settings', icon: 'category' },
		COMPONENT_CREATED: { category: 'settings', icon: 'component' },
		COMPONENT_UPDATED: { category: 'settings', icon: 'component' },
		DOCUMENT_TYPE_CREATED: { category: 'settings', icon: 'documentType' },
		DOCUMENT_TYPE_UPDATED: { category: 'settings', icon: 'documentType' },
		EXPENSE_TYPE_CREATED: { category: 'settings', icon: 'expenseType' },
		EXPENSE_TYPE_UPDATED: { category: 'settings', icon: 'expenseType' },
		BENEFIT_CREATED: { category: 'settings', icon: 'nonCashBenefit' },
		BENEFIT_UPDATED: { category: 'settings', icon: 'nonCashBenefit' },
		PROGRAM_CREATED: { category: 'settings', icon: 'other' },
		PROGRAM_UPDATED: { category: 'settings', icon: 'other' },
		TAG_CREATED: { category: 'settings', icon: 'tag' },
		TAG_UPDATED: { category: 'settings', icon: 'tag' },
	};
	const keysNotification = Object.keys(notificationMapping);
	return notifications.map((notification) => {
		const key = toCamelCase(notification.type);
		const mapping = keysNotification.find((type) => notification.type === type) || '';

		return {
			...notification,
			category: notificationMapping[mapping as NotificationType]?.category || null,
			description: t(`notifications.${key}.description`),
			isEnabled: false,
			icon: notificationMapping[mapping as NotificationType]?.icon || null,
			label: t(`notifications.${key}.name`),
		};
	});
};

export const Notifications = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();
	const { project } = useContext(ContextProject);

	// context
	const { user } = useContext(ContextUser);

	// params
	const paramDialog = searchParams.get('dialog');

	// state
	const [tabValue, setTabValue] = useState('all');
	const [notifications, setNotifications] = useState<NotificationSetting[]>([]);
	const [isToggleAll, setIsToggleAll] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const handleToggleAll = () => {
		setIsToggleAll((prev) => !prev);
		setNotifications((prevNotifications) => {
			return prevNotifications.map((notification) => {
				return {
					...notification,
					isEnabled: !isToggleAll,
				};
			});
		});
	};

	// vars
	const tabs = [
		{
			label: t('all'),
			value: 'all',
			count: notifications.length,
		},
		{
			label: t('documents'),
			value: 'documents',
			count: notifications.filter((n) => n.category === 'documents').length,
		},
		{
			label: t('transactions'),
			value: 'transactions',
			count: notifications.filter((n) => n.category === 'transactions').length,
		},
		{
			label: t('entities'),
			value: 'entities',
			count: notifications.filter((n) => n.category === 'entities').length,
		},
		{
			label: t('accounts'),
			value: 'accounts',
			count: notifications.filter((n) => n.category === 'accounts').length,
		},
	];

	if (featureFlags['ACTIVITIES']) {
		tabs.push({
			label: t('activities'),
			value: 'activities',
			count: notifications.filter((n) => n.category === 'activities').length,
		});
	}

	tabs.push(
		{
			label: t('contracts'),
			value: 'contracts',
			count: notifications.filter((n) => n.category === 'contracts').length,
		},
		{
			label: t('planning'),
			value: 'planning',
			count: notifications.filter((n) => n.category === 'planning').length,
		},
		{
			label: t('settings'),
			value: 'settings',
			count: notifications.filter((n) => n.category === 'settings').length,
		}
	);

	useEffect(() => {
		const fetchNotifications = async () => {
			try {
				setIsLoading(true);

				// Fetch notifications and user data concurrently
				const [resNotifications, resUserNotifications] = await Promise.all([
					fetch(`${process.env.REACT_APP_API_URL}/notifications`, {
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					}).then((res) => res.json()),
					fetch(`${process.env.REACT_APP_API_URL}/users/${user?.id}`, {
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					}).then((res) => res.json()),
				]);

				// Process notifications, excluding contract and invoice types.
				// These are required transactional emails for the workflow, not
				// optional preferences a user can opt in or out of.
				const fetchedNotifications = getNotificationMetadata(
					resNotifications.data,
					t
				).filter(
					(notification) =>
						!notification.type.startsWith('CONTRACT_') &&
						!notification.type.startsWith('INVOICE_')
				);

				// Map user notifications to the notifications state
				if (!resUserNotifications.data?.[0]) throw Error(t('userNotFound'));
				const userNotifications = resUserNotifications.data[0].notifications || [];
				const updatedNotifications = fetchedNotifications.map((notification) => ({
					...notification,
					isEnabled: userNotifications.some(
						(userNotification: { notification: number; project: number }) =>
							userNotification.notification === notification.id &&
							project &&
							userNotification.project === project?.id
					),
				}));

				setNotifications(updatedNotifications);
			} catch (error) {
				console.error('Error fetching notifications or user data:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchNotifications();
	}, [accessToken, idToken, project, t, user?.id]);

	useEffect(() => {
		// Check if all notifications are enabled to set toggleAll state
		const allEnabled =
			notifications.length > 0 && notifications.every((notification) => notification.isEnabled);
		setIsToggleAll(allEnabled);
	}, [notifications]);

	// handlers
	const handleChangeTab = (e: MouseEvent<HTMLButtonElement>, value: string) => {
		setTabValue(value);
	};

	const handleToggleNotification = (notificationId: number) => {
		setNotifications((prevNotifications) =>
			prevNotifications.map((notification) =>
				notification.id === notificationId
					? { ...notification, isEnabled: !notification.isEnabled }
					: notification
			)
		);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		setSearchParams(searchParams);
	};

	// render
	const renderNotifications = (type: string) => {
		const filteredNotifications =
			type === 'all'
				? notifications
				: notifications.filter((notification) => notification.category === type);

		return filteredNotifications
			.sort((a, b) => a.type.localeCompare(b.type))
			.map((notification) => (
				<Grid item xs={12} key={notification.id}>
					<Switch
						BoxProps={{ sx: { padding: 2 } }}
						size="small"
						checked={notification.isEnabled}
						onChange={() => handleToggleNotification(notification.id)}
						label={notification.label}
						description={{ children: notification.description }}
						IconCircleProps={{ type: notification.icon as IconCircleProps['type'] }}
					/>
				</Grid>
			));
	};

	return (
		<>
			<Container maxWidth={false}>
				<TabContext value={tabValue}>
					<Grid container spacing={2}>
						<Grid item xs={12} lg={4}>
							<Paper
								sx={(theme) => ({
									padding: theme.spacing(2),
									height: '100%',
									minHeight: {
										lg: 400,
									},
								})}>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<Typography variant="h3">{t('notificationType')}</Typography>
									</Grid>

									<Grid item xs={12}>
										<Grid
											container
											spacing={2}
											direction="row"
											justifyContent="space-between"
											alignItems="center">
											{tabs.map((tab) => (
												<Grid item key={tab.value} xs={12}>
													<BtnBox
														children={tab.label}
														count={tab.count}
														isActive={tabValue === tab.value}
														onClick={(e) => handleChangeTab(e, tab.value)}
													/>
												</Grid>
											))}
										</Grid>
									</Grid>
								</Grid>
							</Paper>
						</Grid>
						<Grid item xs={12} lg={8}>
							<Paper
								sx={(theme) => ({
									padding: theme.spacing(2),
									height: '100%',
								})}>
								<Grid
									alignItems="stretch"
									container
									direction="column"
									spacing={2}
									sx={{ height: 'calc(100% + 16px)' }}>
									<Grid alignItems="flex-start" item container justifyContent="space-between">
										<Typography variant="h3">{t('emailNotifications')}</Typography>
										<Grid item>
											<Switch
												BoxProps={{
													sx: {
														border: 'none',
														padding: theme.spacing(0, 2),
													},
												}}
												checked={isToggleAll}
												GridProps={{
													spacing: 1,
												}}
												id="toggle-all"
												label={t('all')}
												name="toggle-all"
												onChange={handleToggleAll}
												size="small"
											/>
										</Grid>
									</Grid>

									<Grid item flex={1}>
										{isLoading ? (
											<Grid
												container
												justifyContent="center"
												alignItems="center"
												sx={{ height: '100%' }}>
												<CircularProgress />
											</Grid>
										) : (
											<>
												<TabPanel value="all">
													<Grid container spacing={2}>
														{renderNotifications('all')}
													</Grid>
												</TabPanel>
												{tabs
													.filter((tab) => tab.value !== 'all')
													.map((tab) => (
														<TabPanel value={tab.value} key={tab.value}>
															<Grid container spacing={2}>
																{renderNotifications(tab.value)}
															</Grid>
														</TabPanel>
													))}
											</>
										)}
									</Grid>
								</Grid>
							</Paper>
						</Grid>
					</Grid>
				</TabContext>
			</Container>
			<DialogUpdateUserNotifications
				isOpen={paramDialog === 'updateUserNotifications'}
				notifications={notifications}
				onClose={handleCloseDialog}
			/>
		</>
	);
};
